import DeleteIcon from '../components/DeleteIcon';
import ManutencaoEvidencia, { eVideo, MAX_VIDEO_BYTES, pedidoComVideos } from '../components/ManutencaoEvidencia';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { obterOrdem, submeterFicha, novoIdSubmissao, abrirPdfExterno, urlPdfExterno } from '../services/manutencaoPublica';
import { MaquetesThemeProvider, useMaquetesTheme } from '../context/MaquetesThemeContext';
import { OrcamentoIcon as Icon, OrcamentoFeedbackProvider, useOrcamentoFeedback } from '../components/OrcamentoUI';
import './RelatoriosThemes.css';
import './ManutencoesTheme.css';
import './VerificacaoAndaime.css';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

/* ============================================================
   FICHA DE MANUTENÇÃO (mobile/tablet)
   Técnico escolhe a obra de manutenção, preenche diagnóstico/
   resolução, marca trabalhos executados, junta fotos, assina
   (técnico + responsável de obra) e gera o PDF de prestação
   de serviço de manutenção.
   ============================================================ */

const MAX_PHOTOS = 6;

// Meios de acesso/elevação para trabalhos em altura (escolhido pelo técnico; sai na ficha)
const MEIOS_ELEVATORIOS = [
    'Escada de mão / escadote',
    'Andaime fixo',
    'Andaime móvel (torre)',
    'Plataforma elevatória — tesoura',
    'Plataforma elevatória — articulada/telescópica',
    'Plataforma sobre veículo',
    'Cesto / plataforma suspensa',
];

// Dia de hoje no fuso do tablet (o toISOString dá o dia UTC: logo a seguir à
// meia-noite ainda marcava o dia anterior).
const hojeLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const diaPt = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');

/* Início e fim da intervenção — o relatório leva sempre dia e hora dos dois.
   As mesmas regras do servidor: tudo preenchido e o fim depois do início (o
   fim pode ser noutro dia, p. ex. trabalho que passa da meia-noite). */
const erroPeriodo = (diaIni, hIni, diaFim, hFim) => {
    if (!diaIni || !hIni) return 'Indique o dia e a hora de início da intervenção.';
    if (!hFim) return 'Indique a hora de fim da intervenção.';
    const fim = diaFim || diaIni;
    if (fim < diaIni) return 'O dia de fim da intervenção não pode ser anterior ao de início.';
    if (`${fim}T${hFim}` <= `${diaIni}T${hIni}`) return 'O fim da intervenção tem de ser depois do início.';
    return null;
};
const duracaoTxt = (diaIni, hIni, diaFim, hFim) => {
    if (!diaIni || !hIni || !hFim) return '';
    const min = Math.round((Date.parse(`${diaFim || diaIni}T${hFim}:00Z`) - Date.parse(`${diaIni}T${hIni}:00Z`)) / 60000);
    if (!(min > 0)) return '';
    const d = Math.floor(min / 1440), h = Math.floor((min % 1440) / 60), m = min % 60;
    const real = `${d ? `${d} d ` : ''}${h}h${String(m).padStart(2, '0')}`;
    return min < 60
        ? `Duração considerada — 1 hora. Duração registada: ${real}.`
        : `Duração: ${real}.`;
};

// Redimensionar/comprimir foto no cliente (máx 1280px, JPEG q0.7)
const compressImage = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
            const maxDim = 1280;
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
                const scale = maxDim / Math.max(width, height);
                width = Math.round(width * scale);
                height = Math.round(height * scale);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width; canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = () => resolve(reader.result);
        img.src = reader.result;
    };
    reader.readAsDataURL(file);
});

const FichaManutencaoContent = ({ acessoExterno = false }) => {
    const { theme } = useMaquetesTheme();
    const { notify, confirm } = useOrcamentoFeedback();
    const { user } = useAuth();
    const navigate = useNavigate();
    const { token } = useParams();
    const [nomeTecnico, setNomeTecnico] = useState('');
    const submissaoId = useRef(null);
    const submitting = useRef(false);
    const [erroAcesso, setErroAcesso] = useState('');
    const acessoCarregado = useRef(false);

    const [obras, setObras] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [obraId, setObraId] = useState('');
    const [dataIntervencao, setDataIntervencao] = useState(hojeLocal);
    const [dataFimIntervencao, setDataFimIntervencao] = useState(hojeLocal);
    // O dia de fim acompanha o de início enquanto for o mesmo dia (o caso normal)
    const mudarDataInicio = (v) => {
        setDataFimIntervencao(fim => ((!fim || fim === dataIntervencao || fim < v) ? v : fim));
        setDataIntervencao(v);
    };
    const [diagnostico, setDiagnostico] = useState('');
    const [resolucao, setResolucao] = useState('');
    const [comentarios, setComentarios] = useState('');
    // Sem valor inicial: quem preenche a ficha esteve lá e tem de dizer como ficou
    const [estado, setEstado] = useState('');
    const [trabalhosSel, setTrabalhosSel] = useState(new Set());
    const [trabalhoExtra, setTrabalhoExtra] = useState('');
    const [trabalhosExtra, setTrabalhosExtra] = useState([]);
    // Hora de início/fim e material utilizado — vão para a ficha (PDF) e para o escritório
    const [horaInicio, setHoraInicio] = useState('');
    const [horaFim, setHoraFim] = useState('');
    const [materiais, setMateriais] = useState([]);          // [{ descricao, quantidade }]
    const [materialDesc, setMaterialDesc] = useState('');
    const [materialQtd, setMaterialQtd] = useState('');
    const addMaterial = () => {
        const d = materialDesc.trim();
        if (!d) return;
        setMateriais(prev => [...prev, { descricao: d, quantidade: materialQtd.trim() }]);
        setMaterialDesc(''); setMaterialQtd('');
    };
    const [fotos, setFotos] = useState([]);
    const [videos, setVideos] = useState([]);
    const [aPrepararAnexos, setAPrepararAnexos] = useState(false);
    const anexosEmPreparacao = useRef(false);
    const videoInputRef = useRef(null);
    const [nomeResponsavel, setNomeResponsavel] = useState('');
    // Meio elevatório (trabalhos em altura). 'Outro' revela um campo livre.
    const [meioElevatorio, setMeioElevatorio] = useState('');
    const [meioOutro, setMeioOutro] = useState('');

    const [resultado, setResultado] = useState(null); // { id, relatorio_num, share_token }
    const [fichasObra, setFichasObra] = useState([]);
    const [editandoFicha, setEditandoFicha] = useState(null); // ficha em edição (ou null = nova)
    const [fotosExistentes, setFotosExistentes] = useState([]); // caminhos no servidor (modo edição)
    const [tipoFiltro, setTipoFiltro] = useState('manutencao'); // manutencao | pendencia | todos
    const [anexosObra, setAnexosObra] = useState([]); // anexos do processo (ex.: relatório da fiscalização)
    const [estadoFiltro, setEstadoFiltro] = useState('ativas'); // ativas | concluidas | todas
    const [pesquisaObra, setPesquisaObra] = useState('');

    // Aviso aos intervenientes ao gravar (só pendências)
    const [enviarEmail, setEnviarEmail] = useState(false);
    const [enviarCliente, setEnviarCliente] = useState(true);
    const [enviarFiscalizacao, setEnviarFiscalizacao] = useState(true);
    const [emailsExtra, setEmailsExtra] = useState([]);
    const [emailExtraInput, setEmailExtraInput] = useState('');

    // Base para servir as fotos existentes (ficheiros em /uploads, fora do /api)
    const uploadsBase = api.defaults.baseURL.replace(/\/api\/?$/, '');

    const sigTecnicoRef = useRef(null);
    const sigResponsavelRef = useRef(null);
    const cameraInputRef = useRef(null);
    const galeriaInputRef = useRef(null);

    const fetchObras = useCallback(async (silent = false) => {
        if (!silent) setIsLoading(true);
        try {
            if (acessoExterno) {
                const r = await obterOrdem(token);
                setObras([r.obra]);
                setObraId(String(r.obra.id));
                setErroAcesso('');
                acessoCarregado.current = true;
                return;
            }
            // todas=1: traz também concluídas (o filtro é feito aqui);
            // _t: cache-buster — o Safari iOS reutilizava respostas GET antigas na PWA
            const r = await api.get('/manutencao/obras', { params: { todas: 1, _t: Date.now() } });
            setObras(r.data || []);
        } catch (err) {
            if (acessoExterno) {
                if (!acessoCarregado.current || err.response?.status === 404) {
                    setErroAcesso(err.response?.data?.error || 'Não foi possível abrir a ordem. Verifique a ligação e tente novamente.');
                } else {
                    setMessage({ type: 'error', text: 'Não foi possível atualizar a ordem. Os dados preenchidos mantêm-se; verifique a ligação antes de enviar.' });
                }
                return;
            }
            console.error(err);
            if (!silent) setMessage({ type: 'error', text: 'Não foi possível carregar as manutenções.' });
        } finally { if (!silent) setIsLoading(false); }
    }, [acessoExterno, token]);

    useEffect(() => { fetchObras(); }, [fetchObras]);

    // Atualizar automaticamente quando a app volta ao primeiro plano (PWA/tab)
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') fetchObras(true); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [fetchObras]);

    const obraSel = obras.find(o => o.id === parseInt(obraId));
    const ehPendencia = obraSel?.tipo_obra === 'pendencia';

    // Pré-preencher o responsável de obra quando muda a obra
    useEffect(() => {
        if (obraSel?.responsavel_nome) setNomeResponsavel(obraSel.responsavel_nome);
    }, [obraId]); // eslint-disable-line

    // Numa pendência resolvida o aviso vem marcado (é o que sempre aconteceu);
    // nos outros estados fica ao critério de quem grava.
    useEffect(() => {
        setEnviarEmail(ehPendencia && estado === 'concluida');
    }, [ehPendencia, estado]);

    const juntarEmailExtra = () => {
        const e = emailExtraInput.trim();
        if (!e) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setMessage({ type: 'error', text: 'Email inválido.' }); return; }
        if (!emailsExtra.includes(e)) setEmailsExtra([...emailsExtra, e]);
        setEmailExtraInput('');
    };

    // Fichas já criadas neste processo (para consultar/editar)
    const carregarFichasObra = useCallback(async (id) => {
        if (acessoExterno) return;
        if (!id) { setFichasObra([]); return; }
        try {
            const r = await api.get('/manutencao', { params: { obra_id: id, _t: Date.now() } });
            setFichasObra(r.data || []);
        } catch { setFichasObra([]); }
    }, [acessoExterno]);
    useEffect(() => { carregarFichasObra(obraId); }, [obraId, carregarFichasObra]);

    // Anexos do processo (documentos enviados pelo escritório para o técnico consultar)
    useEffect(() => {
        if (acessoExterno) return;
        if (!obraId) { setAnexosObra([]); return; }
        api.get(`/obra-anexos/${obraId}`, { params: { _t: Date.now() } })
            .then(r => setAnexosObra(r.data || []))
            .catch(() => setAnexosObra([]));
    }, [obraId, acessoExterno]);

    const abrirAnexo = async (a) => {
        try {
            const res = await api.get(`/obra-anexos/ficheiro/${a.id}`, { responseType: 'blob' });
            window.open(window.URL.createObjectURL(res.data), '_blank');
        } catch { notify('Erro ao abrir o anexo'); }
    };

    const iniciarEdicao = (f) => {
        setEditandoFicha(f);
        let ex = [];
        try { ex = typeof f.fotos === 'string' ? JSON.parse(f.fotos || '[]') : (f.fotos || []); } catch { ex = []; }
        setFotosExistentes(Array.isArray(ex) ? ex : []);
        setFotos([]); setVideos([]);
        const diaIni = f.data_intervencao ? String(f.data_intervencao).substring(0, 10) : hojeLocal();
        setDataIntervencao(diaIni);
        setDataFimIntervencao(f.data_fim_intervencao ? String(f.data_fim_intervencao).substring(0, 10) : diaIni);
        // O estado mostrado é o do processo (a ficha acompanha-o)
        setEstado(['em_curso', 'parcial', 'concluida'].includes(obraSel?.status) ? obraSel.status : (f.estado || ''));
        setDiagnostico(f.diagnostico || '');
        setHoraInicio(f.hora_inicio ? String(f.hora_inicio).substring(0, 5) : '');
        setHoraFim(f.hora_fim ? String(f.hora_fim).substring(0, 5) : '');
        setMateriais(Array.isArray(f.materiais) ? f.materiais : []);
        setResolucao(f.resolucao || '');
        setComentarios(f.comentarios || '');
        setNomeResponsavel(f.nome_responsavel || '');
        const me = f.meio_elevatorio || '';
        if (me && !MEIOS_ELEVATORIOS.includes(me)) { setMeioElevatorio('Outro'); setMeioOutro(me); }
        else { setMeioElevatorio(me); setMeioOutro(''); }
        const nomes = Array.isArray(f.trabalhos_executados) ? f.trabalhos_executados : [];
        const daObra = obraSel?.tipo_trabalho || [];
        setTrabalhosSel(new Set(nomes.filter(n => daObra.includes(n))));
        setTrabalhosExtra(nomes.filter(n => !daObra.includes(n)));
        setMessage({ type: '', text: '' });
        window.scrollTo(0, 0);
    };

    const cancelarEdicao = () => {
        setEditandoFicha(null);
        setDiagnostico(''); setResolucao(''); setComentarios('');
        setHoraInicio(''); setHoraFim(''); setMateriais([]); setMaterialDesc(''); setMaterialQtd('');
        setTrabalhosSel(new Set()); setTrabalhosExtra([]);
        setFotos([]); setVideos([]); setFotosExistentes([]);
        setEstado('');
        setMeioElevatorio(''); setMeioOutro('');
        setDataIntervencao(hojeLocal()); setDataFimIntervencao(hojeLocal());
        if (obraSel?.responsavel_nome) setNomeResponsavel(obraSel.responsavel_nome);
    };

    const abrirPdfFicha = async (f) => {
        try {
            const res = await api.get(`/manutencao/relatorio/${f.id}`, { responseType: 'blob' });
            window.open(window.URL.createObjectURL(res.data), '_blank');
        } catch { notify('Erro ao abrir o PDF'); }
    };

    // Link partilhável do relatório (abre sem login, via share token)
    const linkFicha = (f) => `${window.location.origin}/api/manutencao/relatorio/${f.id}?t=${f.share_token}`;

    const partilharFicha = async (f) => {
        const url = linkFicha(f);
        try {
            if (navigator.share) {
                await navigator.share({ title: `Relatório ${f.relatorio_num}`, text: `Relatório ${f.relatorio_num}`, url });
            } else {
                await navigator.clipboard.writeText(url);
                notify('Link copiado para a área de transferência:\n' + url);
            }
        } catch (err) {
            if (err?.name !== 'AbortError') {
                try { await navigator.clipboard.writeText(url); notify('Link copiado:\n' + url); }
                catch { window.prompt('Copie o link do relatório:', url); }
            }
        }
    };

    const ESTADO_MINI = {
        aguardar: { l: 'A aguardar', bg: '#fee2e2', fg: '#991b1b' },
        em_curso: { l: 'Em curso', bg: '#e0f2fe', fg: '#075985' },
        parcial: { l: 'Parcial', bg: '#fef9c3', fg: '#854d0e' },
        concluida: { l: 'Concluída', bg: '#dcfce7', fg: '#166534' },
    };

    const toggleTrabalho = (nome) => {
        setTrabalhosSel(prev => {
            const next = new Set(prev);
            if (next.has(nome)) next.delete(nome); else next.add(nome);
            return next;
        });
    };

    const addTrabalhoExtra = () => {
        const t = trabalhoExtra.trim();
        if (!t) return;
        setTrabalhosExtra(prev => [...prev, t]);
        setTrabalhoExtra('');
    };

    const handlePhotoUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (anexosEmPreparacao.current || isSubmitting) return;
        const jaTem = fotosExistentes.length + fotos.length + videos.length;
        if (jaTem + files.length > MAX_PHOTOS) {
            notify('Pode juntar no máximo 6 evidências (fotos e vídeos).');
            return;
        }
        for (const file of files) {
            if (eVideo(file.name)) {
                if (file.size > MAX_VIDEO_BYTES) { notify('Cada vídeo pode ter no máximo 100 MB.'); return; }
            } else if (!file.type.startsWith('image/')) {
                notify('Selecione uma imagem ou um vídeo MP4, MOV, M4V, WebM ou 3GP.'); return;
            }
        }
        anexosEmPreparacao.current = true;
        setAPrepararAnexos(true);
        try {
            const novasFotos = [];
            const novosVideos = [];
            for (const file of files) {
                if (eVideo(file.name)) novosVideos.push(file);
                else novasFotos.push(await compressImage(file));
            }
            setFotos(prev => [...prev, ...novasFotos]);
            setVideos(prev => [...prev, ...novosVideos]);
        } catch (_) { notify('Não foi possível preparar os anexos. Tente novamente.'); }
        finally { anexosEmPreparacao.current = false; setAPrepararAnexos(false); }
    };

    const removePhoto = (idx) => setFotos(prev => prev.filter((_, i) => i !== idx));

    const limparAssinatura = (ref) => ref.current?.clear();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (anexosEmPreparacao.current || isSubmitting || submitting.current || (acessoExterno && erroAcesso)) return;
        if (!obraId) { setMessage({ type: 'error', text: 'Selecione a obra/manutenção.' }); return; }

        const trabalhos = [...Array.from(trabalhosSel), ...trabalhosExtra];
        const meioElevatorioFinal = (meioElevatorio === 'Outro' ? meioOutro.trim() : meioElevatorio) || null;
        if (trabalhos.length === 0 && !resolucao.trim() && !diagnostico.trim()) {
            setMessage({ type: 'error', text: 'Preencha o diagnóstico/resolução ou marque trabalhos executados.' });
            return;
        }
        if (!estado) {
            setMessage({ type: 'error', text: 'Indique como ficou a intervenção: em curso, parcialmente resolvida ou concluída.' });
            window.scrollTo(0, 0);
            return;
        }
        const erroPer = erroPeriodo(dataIntervencao, horaInicio, dataFimIntervencao, horaFim);
        if (erroPer) {
            setMessage({ type: 'error', text: erroPer });
            window.scrollTo(0, 0);
            return;
        }

        // Modo edição: atualiza a ficha existente (fotos e assinaturas originais mantêm-se)
        if (editandoFicha) {
            setIsSubmitting(true);
            setMessage({ type: '', text: 'A atualizar a ficha…' });
            try {
                const upd = await api.put(`/manutencao/${editandoFicha.id}`, pedidoComVideos({
                    data_intervencao: dataIntervencao,
                    data_fim_intervencao: dataFimIntervencao || dataIntervencao,
                    hora_inicio: horaInicio || null, hora_fim: horaFim || null, materiais,
                    meio_elevatorio: meioElevatorioFinal,
                    estado, diagnostico, resolucao, comentarios,
                    trabalhos_executados: trabalhos,
                    nome_tecnico: editandoFicha.nome_tecnico || user?.nome_completo || user?.username || 'Técnico',
                    nome_responsavel: nomeResponsavel,
                    fotos_manter: fotosExistentes,
                    fotos_base64: fotos,
                    origem: window.location.origin, // base do link no email (como no onboarding)
                    // aviso aos intervenientes: só nas pendências há a quem enviar
                    ...(ehPendencia ? {
                        enviar_email: enviarEmail,
                        enviar_cliente: enviarCliente,
                        enviar_fiscalizacao: enviarFiscalizacao,
                        emails_extra: emailsExtra,
                    } : {}),
                }, videos), { timeout: videos.length ? 180000 : 60000 });
                setResultado({ id: editandoFicha.id, relatorio_num: editandoFicha.relatorio_num, share_token: editandoFicha.share_token });
                setMessage({
                    type: 'success',
                    text: `Ficha ${editandoFicha.relatorio_num} atualizada — PDF regenerado.`
                        + (upd.data?.email_aviso ? ` ✉️ ${upd.data.email_aviso}` : ''),
                });
                setEditandoFicha(null);
                setFotos([]); setVideos([]); setFotosExistentes([]);
                carregarFichasObra(obraId);
                fetchObras(true);
                window.scrollTo(0, 0);
            } catch (err) {
                console.error(err);
                setMessage({ type: 'error', text: 'Erro ao atualizar: ' + (err.response?.data?.error || err.message) });
            } finally { setIsSubmitting(false); }
            return;
        }

        const sigTecnico = sigTecnicoRef.current && !sigTecnicoRef.current.isEmpty()
            ? sigTecnicoRef.current.getCanvas().toDataURL('image/png') : null;
        const sigResponsavel = sigResponsavelRef.current && !sigResponsavelRef.current.isEmpty()
            ? sigResponsavelRef.current.getCanvas().toDataURL('image/png') : null;

        if (acessoExterno && (!nomeTecnico.trim() || !nomeResponsavel.trim() || !sigTecnico || !sigResponsavel)) {
            setMessage({ type: 'error', text: 'Indique os nomes e recolha as assinaturas do técnico e do responsável.' });
            return;
        }
        if (!sigTecnico) {
            if (!(await confirm('A assinatura do técnico está vazia. Submeter mesmo assim?'))) return;
        }

        setIsSubmitting(true);
        setMessage({ type: '', text: 'A submeter a ficha…' });
        submitting.current = true;
        try {
            const payload = {
                obra_id: parseInt(obraId),
                data_intervencao: dataIntervencao,
                data_fim_intervencao: dataFimIntervencao || dataIntervencao,
                hora_inicio: horaInicio || null, hora_fim: horaFim || null, materiais,
                meio_elevatorio: meioElevatorioFinal,
                estado,
                diagnostico, resolucao, comentarios,
                trabalhos_executados: trabalhos,
                fotos_base64: fotos,
                nome_tecnico: acessoExterno ? nomeTecnico.trim() : user?.nome_completo || user?.nome || user?.username || 'Técnico',
                assinatura_tecnico: sigTecnico,
                nome_responsavel: nomeResponsavel,
                assinatura_responsavel: sigResponsavel,
                origem: window.location.origin, // base do link no email (como no onboarding)
            };
            if (acessoExterno && !submissaoId.current) submissaoId.current = novoIdSubmissao();
            const r = acessoExterno
                ? { data: await submeterFicha(token, { ...payload, submissao_id: submissaoId.current }, videos) }
                : await api.post('/manutencao', pedidoComVideos(payload, videos), { timeout: videos.length ? 180000 : 60000 });
            setResultado(r.data);
            setMessage({
                type: 'success',
                text: r.data?.email_aviso
                    ? `Ficha submetida! ✉️ ${r.data.email_aviso}`
                    : 'Ficha de manutenção submetida com sucesso!',
            });
            carregarFichasObra(obraId);
            fetchObras(true);
            window.scrollTo(0, 0);
        } catch (err) {
            if (!acessoExterno) console.error(err);
            if (acessoExterno && err.response?.status === 404) setErroAcesso(err.response.data.error);
            setMessage({ type: 'error', text: 'Erro ao submeter: ' + (err.response?.data?.error || err.message) });
        } finally { setIsSubmitting(false); submitting.current = false; }
    };

    const pdfPath = resultado ? `/manutencao/relatorio/${resultado.id}?t=${resultado.share_token}` : null;

    const abrirPdf = async () => {
        if (acessoExterno) { abrirPdfExterno(resultado); return; }
        try {
            setMessage({ type: '', text: 'A carregar PDF…' });
            const res = await api.get(pdfPath, { responseType: 'blob' });
            window.open(window.URL.createObjectURL(res.data), '_blank');
            setMessage({ type: '', text: '' });
        } catch (err) {
            notify('Não foi possível abrir o PDF. ' + (err.response?.data?.error || err.message));
        }
    };

    const partilhar = async () => {
        const url = acessoExterno ? urlPdfExterno(resultado) : window.location.origin + '/api' + pdfPath;
        try {
            if (navigator.share) {
                await navigator.share({ title: 'Prestação de Serviço de Manutenção', url });
            } else {
                await navigator.clipboard.writeText(url);
                notify('Link copiado para a área de transferência.');
            }
        } catch (err) { console.error(err); }
    };

    const novaFicha = () => {
        setResultado(null);
        setEditandoFicha(null);
        setEstado('');
        if (!acessoExterno) setObraId('');
        submissaoId.current = null;
        setDiagnostico(''); setResolucao(''); setComentarios('');
        setHoraInicio(''); setHoraFim(''); setMateriais([]); setMaterialDesc(''); setMaterialQtd('');
        setTrabalhosSel(new Set()); setTrabalhosExtra([]); setFotos([]); setVideos([]);
        setNomeResponsavel('');
        setMeioElevatorio(''); setMeioOutro('');
        setMessage({ type: '', text: '' });
        window.scrollTo(0, 0);
    };

    const sigBoxStyle = { border: '1px dashed #a0aec0', borderRadius: 8, background: '#fff', width: 'fit-content', maxWidth: '100%' };
    // Canvas com dimensões fixas (CSS a escalar desalinhava o traço no touch)
    const sigWidth = Math.min((typeof window !== 'undefined' ? window.innerWidth : 500) - 76, 520);

    return (
        <div className="verificacao-andaimes-container ficha-manutencao-page" data-report-theme={theme === 'liquido' ? 'liquido' : undefined}>
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><Icon name="maintenance" fallback="🔧" /> Ficha de Manutenção</h1>
                        <p className="mobile-hide">Diagnóstico e prestação de serviço</p>
                    </div>
                </div>
                {!acessoExterno && <div className="top-bar-actions">
                    {user?.perfil_mobile ? (
                        <button className="btn-secondary" onClick={() => navigate('/mobile-dashboard')}><Icon name="back" fallback="←" /> Menu</button>
                    ) : (
                        <button className="btn-secondary" onClick={() => navigate(-1)}><Icon name="back" fallback="←" /> Voltar</button>
                    )}
                </div>}
            </div>

            <div className="page-content" style={{ padding: 20 }}>
                <div className="va-content-wrapper">
                    {message.text && <div role="status" className={`va-message ${message.type}`}>{message.text}</div>}

                    {acessoExterno && erroAcesso ? (
                        <div role="alert" className="va-form-group">
                            <p>{erroAcesso}</p>
                            <button type="button" className="btn-secondary" disabled={isLoading} onClick={() => fetchObras()}>Tentar novamente</button>
                        </div>
                    ) : acessoExterno && !obraSel ? <p role="status">A carregar a ordem…</p> : resultado ? (
                        <div className="va-success-actions">
                            <p className="success-text"><Icon name="check" fallback="✔️" /> Ficha {resultado.relatorio_num} registada!</p>
                            <div className="btn-group" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <button onClick={abrirPdf} className="va-action-btn download-btn">
                                    <Icon name="document" fallback="📄" /> Ver / Imprimir PDF
                                </button>
                                <button onClick={partilhar} className="va-action-btn share-btn">
                                    <Icon name="send" fallback="📤" /> Partilhar Link
                                </button>
                            </div>
                            <button onClick={novaFicha} className="va-action-btn secondary-btn" style={{ marginTop: 15 }}>
                                <Icon name="plus" fallback="➕" /> Nova Ficha
                            </button>
                        </div>
                    ) : (
                        <form className="va-form" onSubmit={handleSubmit}>
                            {(() => {
                                {/* Um só estado: o do processo (pendência/manutenção) */}
                                const isConcluida = (o) => ['concluida', 'recebida'].includes(o.status);
                                const EST = {
                                    planeada: { l: 'A aguardar', bg: '#fee2e2', fg: '#991b1b' },
                                    em_curso: { l: 'Em curso', bg: '#e0f2fe', fg: '#075985' },
                                    parcial: { l: 'Parcial', bg: '#fef9c3', fg: '#854d0e' },
                                    concluida: { l: 'Concluída', bg: '#dcfce7', fg: '#166534' },
                                    suspensa: { l: 'Suspensa', bg: '#f1f5f9', fg: '#64748b' },
                                    recebida: { l: 'Recebida', bg: '#ede9fe', fg: '#5b21b6' },
                                };
                                const chip = (ativo) => ({
                                    padding: '8px 14px', borderRadius: 16, fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                                    border: '1px solid ' + (ativo ? '#2b6cb0' : '#cbd5e0'),
                                    background: ativo ? '#2b6cb0' : '#fff', color: ativo ? '#fff' : '#334155',
                                });

                                // Manutenção escolhida → cartão compacto com opção de trocar
                                if (obraSel) {
                                    const ue = EST[obraSel.status] || null;
                                    return (
                                        <div className="va-form-group">
                                            <label>Obra / Manutenção*</label>
                                            <div className="mnt-selected-process" style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#ebf8ff', border: '1px solid #90cdf4', borderRadius: 10, padding: '10px 12px', flexWrap: 'wrap' }}>
                                                <div style={{ flex: 1, minWidth: 160 }}>
                                                    <div style={{ fontWeight: 700 }}>{obraSel.posicao}{obraSel.descricao ? ` — ${obraSel.descricao}` : ''}</div>
                                                    <small style={{ color: '#4a5568' }}>{obraSel.cliente || ''}</small>
                                                </div>
                                                {ue && <span className="mnt-mobile-status" style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: ue.bg, color: ue.fg }}>{ue.l}</span>}
                                                {!editandoFicha && !acessoExterno && (
                                                    <button type="button" onClick={() => { setObraId(''); setPesquisaObra(''); }}
                                                        style={{ border: '1px solid #cbd5e0', background: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}>
                                                        ↺ Trocar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }

                                // Sem escolha → lista filtrável (1º por tipo, depois por estado)
                                const doTipo = obras.filter(o => tipoFiltro === 'todos' ? true : (o.tipo_obra || 'manutencao') === tipoFiltro);
                                let lista = doTipo.filter(o =>
                                    estadoFiltro === 'todas' ? true : estadoFiltro === 'concluidas' ? isConcluida(o) : !isConcluida(o));
                                if (pesquisaObra.trim()) {
                                    const t = pesquisaObra.trim().toLowerCase();
                                    lista = lista.filter(o =>
                                        (o.posicao || '').toLowerCase().includes(t) || (o.descricao || '').toLowerCase().includes(t) ||
                                        (o.cliente || '').toLowerCase().includes(t) || (o.observacoes || '').toLowerCase().includes(t));
                                }
                                const nAtivas = doTipo.filter(o => !isConcluida(o)).length;
                                const nConcl = doTipo.length - nAtivas;
                                const nManut = obras.filter(o => (o.tipo_obra || 'manutencao') === 'manutencao').length;
                                const nPend = obras.filter(o => o.tipo_obra === 'pendencia').length;

                                return (
                                    <div className="va-form-group">
                                        <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>Obra / Manutenção*</span>
                                            <button type="button" onClick={() => fetchObras()} disabled={isLoading}
                                                style={{ border: '1px solid #cbd5e0', background: '#fff', borderRadius: 6, padding: '4px 10px', fontSize: 13, cursor: 'pointer' }}>
                                                {isLoading ? 'A atualizar…' : '🔄 Atualizar'}
                                            </button>
                                        </label>
                                        {/* Nível 1 — tipo de intervenção */}
                                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
                                            <button type="button" className="mnt-chip" aria-pressed={tipoFiltro === 'manutencao'} style={{ ...chip(tipoFiltro === 'manutencao'), padding: '10px 16px', fontSize: 14 }} onClick={() => setTipoFiltro('manutencao')}><Icon name="maintenance" fallback="🔧" /> Manutenção ({nManut})</button>
                                            <button type="button" className="mnt-chip" aria-pressed={tipoFiltro === 'pendencia'} style={{ ...chip(tipoFiltro === 'pendencia'), padding: '10px 16px', fontSize: 14 }} onClick={() => setTipoFiltro('pendencia')}><Icon name="warning" fallback="📌" /> Pendências ({nPend})</button>
                                            <button type="button" className="mnt-chip" aria-pressed={tipoFiltro === 'todos'} style={{ ...chip(tipoFiltro === 'todos'), padding: '10px 16px', fontSize: 14 }} onClick={() => setTipoFiltro('todos')}>Todos ({obras.length})</button>
                                        </div>
                                        {/* Nível 2 — estado */}
                                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
                                            <button type="button" className="mnt-chip" aria-pressed={estadoFiltro === 'ativas'} style={chip(estadoFiltro === 'ativas')} onClick={() => setEstadoFiltro('ativas')}>Ativas ({nAtivas})</button>
                                            <button type="button" className="mnt-chip" aria-pressed={estadoFiltro === 'concluidas'} style={chip(estadoFiltro === 'concluidas')} onClick={() => setEstadoFiltro('concluidas')}><Icon name="check" fallback="🟢" /> Concluídas ({nConcl})</button>
                                            <button type="button" className="mnt-chip" aria-pressed={estadoFiltro === 'todas'} style={chip(estadoFiltro === 'todas')} onClick={() => setEstadoFiltro('todas')}>Todas ({doTipo.length})</button>
                                        </div>
                                        <input value={pesquisaObra} onChange={e => setPesquisaObra(e.target.value)}
                                            placeholder="🔎 Pesquisar posição, cliente, descritivo…"
                                            style={{ marginBottom: 8 }} />
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto' }}>
                                            {lista.length === 0 && (
                                                <div style={{ color: '#94a3b8', fontSize: 14, textAlign: 'center', padding: '18px 0' }}>
                                                    Sem manutenções {estadoFiltro === 'concluidas' ? 'concluídas' : estadoFiltro === 'ativas' ? 'ativas' : ''} para mostrar.
                                                </div>
                                            )}
                                            {lista.map(o => {
                                                const ue = EST[o.status] || null;
                                                return (
                                                    <button className="mnt-choose-process" key={o.id} type="button" onClick={() => setObraId(String(o.id))}
                                                        style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 12px', cursor: 'pointer', flexWrap: 'wrap' }}>
                                                        <div style={{ flex: 1, minWidth: 150 }}>
                                                            <div style={{ fontWeight: 700, fontSize: 14.5 }}>
                                                                {tipoFiltro === 'todos' && (
                                                                    <span style={{ fontSize: 11, fontWeight: 700, color: o.tipo_obra === 'pendencia' ? '#7c3aed' : '#2b6cb0', marginRight: 6 }}>
                                                                        {o.tipo_obra === 'pendencia' ? '📌 PENDÊNCIA' : '🔧 MANUT.'}
                                                                    </span>
                                                                )}
                                                                {o.posicao}{o.descricao ? ` — ${o.descricao}` : ''}
                                                            </div>
                                                            <small style={{ color: '#718096' }}>{o.cliente || '—'}{o.num_fichas ? ` · ${o.num_fichas} ficha(s)` : ''}</small>
                                                            {/* Notas próprias do painel de Manutenção, como no computador. */}
                                                            {o.notas_manutencao && (
                                                                <div style={{ fontSize: 12.5, color: '#7c2d12', marginTop: 3, lineHeight: 1.35 }}>
                                                                    📌 {o.notas_manutencao.substring(0, 120)}{o.notas_manutencao.length > 120 ? '…' : ''}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {ue && <span className="mnt-mobile-status" style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: ue.bg, color: ue.fg }}>{ue.l}</span>}
                                                        {!o.num_fichas && <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: '#f1f5f9', color: '#64748b' }}>Sem ficha</span>}
                                                        <span style={{ color: '#a0aec0', fontWeight: 700 }}>›</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}

                            {obraSel && (<>

                            {obraSel?.notas_manutencao && (
                                <div style={{
                                    background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 10,
                                    padding: '12px 14px', marginBottom: 16, fontSize: 14.5, color: '#7c2d12', lineHeight: 1.45,
                                }}>
                                    <div style={{ fontWeight: 700, marginBottom: 4 }}>📌 Notas próprias</div>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{obraSel.notas_manutencao}</div>
                                </div>
                            )}

                            {obraSel?.observacoes && (
                                <div style={{
                                    background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10,
                                    padding: '12px 14px', marginBottom: 16, fontSize: 14.5, color: '#78350f', lineHeight: 1.45,
                                }}>
                                    <div style={{ fontWeight: 700, marginBottom: 4 }}>📝 Descritivo do trabalho</div>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{obraSel.observacoes}</div>
                                </div>
                            )}

                            {anexosObra.length > 0 && (
                                <div className="va-form-group">
                                    <label>📎 Documentos do processo ({anexosObra.length})</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {anexosObra.map(a => (
                                            <button key={a.id} type="button" onClick={() => abrirAnexo(a)}
                                                style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '12px 14px', cursor: 'pointer', fontSize: 14.5 }}>
                                                <span style={{ fontSize: 20 }}>📄</span>
                                                <span style={{ flex: 1, fontWeight: 600, color: '#166534', wordBreak: 'break-word' }}>{a.nome_original}</span>
                                                <span style={{ color: '#16a34a', fontWeight: 700 }}>Abrir ›</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {editandoFicha && (
                                <div style={{
                                    background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 10,
                                    padding: '10px 14px', marginBottom: 16, fontSize: 14, color: '#1e40af',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                                }}>
                                    <span>✏️ A editar <strong>{editandoFicha.relatorio_num}</strong> — fotos e assinaturas originais mantêm-se.</span>
                                    <button type="button" className="btn-secondary" style={{ padding: '6px 12px', fontSize: 13 }} onClick={cancelarEdicao}>
                                        Cancelar edição
                                    </button>
                                </div>
                            )}

                            {!acessoExterno && obraId && !editandoFicha && fichasObra.length > 0 && (
                                <div className="va-form-group">
                                    <label><Icon name="document" fallback="📋" /> Fichas deste processo ({fichasObra.length})</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {fichasObra.map(f => {
                                            const es = ESTADO_MINI[f.estado] || ESTADO_MINI.aguardar;
                                            return (
                                                <div className="mnt-ficha-row" key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', flexWrap: 'wrap' }}>
                                                    <div style={{ flex: 1, minWidth: 150 }}>
                                                        <div style={{ fontWeight: 700, fontSize: 14 }}>{f.relatorio_num}</div>
                                                        <div style={{ fontSize: 12, color: '#718096' }}>
                                                            {f.data_intervencao ? diaPt(String(f.data_intervencao).slice(0, 10)) : '—'}
                                                            {f.hora_inicio && f.hora_fim ? ` ${String(f.hora_inicio).slice(0, 5)}–${String(f.hora_fim).slice(0, 5)}` : ''}
                                                            {' · '}{f.nome_tecnico || '—'}
                                                        </div>
                                                    </div>
                                                    <span className="mnt-mobile-status" style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: es.bg, color: es.fg }}>{es.l}</span>
                                                    <button type="button" className="mnt-inline-action" onClick={() => abrirPdfFicha(f)} title="Ver PDF"
                                                        style={{ border: '1px solid #cbd5e0', background: '#fff', borderRadius: 8, padding: '7px 10px', fontSize: 13, cursor: 'pointer' }}><Icon name="document" fallback="📄" /></button>
                                                    <button type="button" className="mnt-inline-action" onClick={() => partilharFicha(f)} title="Partilhar link do relatório"
                                                        style={{ border: '1px solid #38a169', background: '#38a169', color: '#fff', borderRadius: 8, padding: '7px 10px', fontSize: 13, cursor: 'pointer' }}><Icon name="copy" fallback="🔗" /></button>
                                                    <button type="button" className="mnt-inline-action" onClick={() => iniciarEdicao(f)}
                                                        style={{ border: '1px solid #2b6cb0', background: '#2b6cb0', color: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}><Icon name="edit" fallback="✏️" /> Editar</button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Início e fim com dia e hora: saem sempre no relatório */}
                            <div className="va-form-group">
                                <label>Início da intervenção *</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 8 }}>
                                    <input type="date" value={dataIntervencao} onChange={e => mudarDataInicio(e.target.value)}
                                        required aria-label="Dia de início da intervenção" />
                                    <input type="time" value={horaInicio} onChange={e => setHoraInicio(e.target.value)}
                                        required aria-label="Hora de início da intervenção" />
                                </div>
                            </div>

                            <div className="va-form-group">
                                <label>Fim da intervenção *</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 8 }}>
                                    <input type="date" value={dataFimIntervencao} min={dataIntervencao || undefined}
                                        onChange={e => setDataFimIntervencao(e.target.value)}
                                        required aria-label="Dia de fim da intervenção" />
                                    <input type="time" value={horaFim} onChange={e => setHoraFim(e.target.value)}
                                        required aria-label="Hora de fim da intervenção" />
                                </div>
                                <small style={{ color: '#718096' }} aria-live="polite">
                                    {duracaoTxt(dataIntervencao, horaInicio, dataFimIntervencao, horaFim)
                                        || 'Se o trabalho passou da meia-noite, mude também o dia de fim.'}
                                </small>
                            </div>

                            <div className="va-form-group">
                                {/* O estado é o do processo: gravar a ficha atualiza a própria pendência/manutenção */}
                                <label>Como fica a {ehPendencia ? 'pendência' : 'manutenção'} *</label>
                                <select value={estado} onChange={e => setEstado(e.target.value)} required style={{
                                    fontWeight: 600,
                                    color: estado === 'concluida' ? '#166534' : estado === 'parcial' ? '#854d0e' : estado === 'em_curso' ? '#075985' : '#991b1b',
                                }}>
                                    <option value="">— indique o estado —</option>
                                    <option value="em_curso">🔵 Em curso (volta-se lá)</option>
                                    <option value="parcial">🟡 Parcialmente resolvida</option>
                                    <option value="concluida">🟢 Concluída</option>
                                    {/* fichas antigas podem ter ficado 'a aguardar' */}
                                    {estado === 'aguardar' && <option value="aguardar">🔴 A aguardar (por rever)</option>}
                                </select>
                                <small style={{ color: estado === 'concluida' ? '#166534' : '#718096' }}>
                                    {estado === 'concluida' && dataFimIntervencao
                                        ? `A ${ehPendencia ? 'pendência' : 'manutenção'} fica concluída a ${diaPt(dataFimIntervencao)} — o dia de fim da intervenção.`
                                        : `Fica registado na própria ${ehPendencia ? 'pendência' : 'manutenção'}, não só nesta ficha.`}
                                </small>
                            </div>

                            {obraSel && (obraSel.tipo_trabalho || []).length > 0 && (
                                <div className="va-form-group">
                                    <label>Trabalhos executados</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {(obraSel.tipo_trabalho || []).map(t => (
                                            <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'normal', background: trabalhosSel.has(t) ? '#ebf8ff' : '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 10px' }}>
                                                <input type="checkbox" checked={trabalhosSel.has(t)} onChange={() => toggleTrabalho(t)} style={{ width: 18, height: 18 }} />
                                                {t}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="va-form-group">
                                <label>Outros trabalhos executados</label>
                                {trabalhosExtra.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                                        {trabalhosExtra.map((t, i) => (
                                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: 14, padding: '4px 10px', fontSize: 13 }}>
                                                {t}
                                                <button type="button" onClick={() => setTrabalhosExtra(prev => prev.filter((_, x) => x !== i))} style={{ border: 'none', background: 'none', color: '#e53e3e', fontWeight: 'bold', cursor: 'pointer', padding: 0 }}>×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input value={trabalhoExtra} onChange={e => setTrabalhoExtra(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTrabalhoExtra(); } }}
                                        placeholder="Descreva e toque em +" style={{ flex: 1 }} />
                                    <button type="button" onClick={addTrabalhoExtra} className="btn-secondary" style={{ padding: '0 16px' }}><Icon name="plus" fallback="➕" /></button>
                                </div>
                            </div>

                            <div className="va-form-group">
                                <label>Meio elevatório (trabalhos em altura)</label>
                                <select value={meioElevatorio} onChange={e => setMeioElevatorio(e.target.value)}>
                                    <option value="">— Sem trabalhos em altura / não aplicável —</option>
                                    {MEIOS_ELEVATORIOS.map(m => <option key={m} value={m}>{m}</option>)}
                                    <option value="Outro">Outro (especificar)</option>
                                </select>
                                {meioElevatorio === 'Outro' && (
                                    <input value={meioOutro} onChange={e => setMeioOutro(e.target.value)}
                                        placeholder="Qual o meio elevatório utilizado?" style={{ marginTop: 8 }} />
                                )}
                                <small style={{ color: '#718096' }}>Se o trabalho foi em altura, indique o meio de acesso/elevação utilizado.</small>
                            </div>

                            <div className="va-form-group">
                                <label>Material utilizado</label>
                                {materiais.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
                                        {materiais.map((m, i) => (
                                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: 10, padding: '6px 10px', fontSize: 13 }}>
                                                <span style={{ flex: 1 }}>{m.descricao}</span>
                                                {m.quantidade && <span style={{ color: '#4a5568', whiteSpace: 'nowrap' }}>{m.quantidade}</span>}
                                                <button type="button" onClick={() => setMateriais(prev => prev.filter((_, x) => x !== i))} style={{ border: 'none', background: 'none', color: '#e53e3e', fontWeight: 'bold', cursor: 'pointer', padding: 0 }}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <input value={materialDesc} onChange={e => setMaterialDesc(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addMaterial(); } }}
                                        placeholder="Material (ex.: fita LED 5m)" style={{ flex: 2 }} />
                                    <input value={materialQtd} onChange={e => setMaterialQtd(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addMaterial(); } }}
                                        placeholder="Qtd." style={{ flex: 1, minWidth: 0 }} />
                                    <button type="button" onClick={addMaterial} className="btn-secondary" style={{ padding: '0 16px' }}><Icon name="plus" fallback="➕" /></button>
                                </div>
                            </div>

                            <div className="va-form-group">
                                <label>Diagnóstico</label>
                                <textarea rows={3} value={diagnostico} onChange={e => setDiagnostico(e.target.value)}
                                    placeholder="Anomalia encontrada / estado inicial…" />
                            </div>

                            <div className="va-form-group">
                                <label>Resolução / trabalhos efetuados</label>
                                <textarea rows={3} value={resolucao} onChange={e => setResolucao(e.target.value)}
                                    placeholder="O que foi feito para resolver…" />
                            </div>

                            <div className="va-form-group">
                                <label>Comentários</label>
                                <textarea rows={2} value={comentarios} onChange={e => setComentarios(e.target.value)}
                                    placeholder="Notas adicionais, recomendações…" />
                            </div>

                            <div className="va-form-group">
                                <fieldset disabled={isSubmitting || aPrepararAnexos} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                                <legend>Fotos e vídeos (evidências) — máx. {MAX_PHOTOS}</legend>
                                {editandoFicha && fotosExistentes.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                                        {fotosExistentes.map((p, idx) => (
                                            <div key={p} style={{ position: 'relative' }}>
                                                <ManutencaoEvidencia src={`${uploadsBase}${p}`} alt={`Evidência existente ${idx + 1}`}
                                                    style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '2px solid #2b6cb0' }} />
                                                <button type="button" title="Remover esta evidência"
                                                    onClick={() => setFotosExistentes(prev => prev.filter(x => x !== p))}
                                                    style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', border: 'none', background: '#e53e3e', color: '#fff', fontWeight: 'bold', cursor: 'pointer', lineHeight: '20px' }}><DeleteIcon /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Câmara direta: input dedicado com capture e SEM multiple (iOS ignora capture com multiple) */}
                                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                                    onChange={handlePhotoUpload} style={{ display: 'none' }} />
                                <input ref={galeriaInputRef} type="file" accept="image/*,video/*" multiple
                                    onChange={handlePhotoUpload} style={{ display: 'none' }} />
                                <input ref={videoInputRef} type="file" accept="video/*" capture="environment" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    <button type="button" className="va-action-btn"
                                        style={{ flex: 1, backgroundColor: '#2b6cb0', color: 'white', padding: '12px 0' }}
                                        disabled={fotosExistentes.length + fotos.length + videos.length >= MAX_PHOTOS}
                                        onClick={() => cameraInputRef.current?.click()}>
                                        <Icon name="camera" fallback="📷" /> Tirar Foto
                                    </button>
                                    <button type="button" className="va-action-btn"
                                        style={{ flex: 1, backgroundColor: '#4a5568', color: 'white', padding: '12px 0' }}
                                        disabled={fotosExistentes.length + fotos.length + videos.length >= MAX_PHOTOS}
                                        onClick={() => galeriaInputRef.current?.click()}>
                                        <Icon name="camera" fallback="🖼️" /> Fotos / vídeos
                                    </button>
                                </div>
                                <button type="button" className="va-action-btn" disabled={fotosExistentes.length + fotos.length + videos.length >= MAX_PHOTOS} onClick={() => videoInputRef.current?.click()} style={{ minHeight: 44, marginTop: 8 }}>Gravar vídeo</button>
                                <small style={{ display: 'block', color: '#718096' }}>{fotosExistentes.length + fotos.length + videos.length}/{MAX_PHOTOS} evidências · Até 100 MB por vídeo. O relatório PDF inclui apenas as fotos.</small>
                                {aPrepararAnexos && <p role="status">A preparar anexos…</p>}
                                {videos.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                                    {videos.map((file, idx) => <div key={idx}>
                                        <ManutencaoEvidencia file={file} alt={`Vídeo ${idx + 1}`} style={{ borderRadius: 8 }} />
                                        <button type="button" onClick={() => setVideos(prev => prev.filter((_, i) => i !== idx))} aria-label={`Remover vídeo ${idx + 1}`} style={{ minHeight: 44 }}><DeleteIcon /> Remover vídeo</button>
                                    </div>)}
                                </div>}
                                {fotos.length > 0 && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                        {fotos.map((f, idx) => (
                                            <div key={idx} style={{ position: 'relative' }}>
                                                <img src={f} alt={`Foto ${idx + 1}`} style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid #cbd5e0' }} />
                                                <button type="button" onClick={() => removePhoto(idx)}
                                                    style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', border: 'none', background: '#e53e3e', color: '#fff', fontWeight: 'bold', cursor: 'pointer', lineHeight: '20px' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                </fieldset>
                            </div>

                            {!editandoFicha && (
                            <div className="va-form-group">
                                {acessoExterno && <label>Nome do técnico*
                                    <input aria-label="Nome do técnico" value={nomeTecnico} onChange={e => setNomeTecnico(e.target.value)} autoComplete="name" required maxLength={200} />
                                </label>}
                                <label>Assinatura do técnico — {acessoExterno ? nomeTecnico : user?.nome_completo || user?.nome || user?.username || ''}</label>
                                <div style={sigBoxStyle}>
                                    <SignatureCanvas ref={sigTecnicoRef} penColor="#1a202c"
                                        canvasProps={{ width: sigWidth, height: 130 }} />
                                </div>
                                <button type="button" className="btn-secondary" style={{ marginTop: 6, padding: '6px 12px', fontSize: 13 }}
                                    onClick={() => limparAssinatura(sigTecnicoRef)}><Icon name="clear" fallback="🧽" /> Limpar</button>
                            </div>
                            )}

                            <div className="va-form-group">
                                <label>Responsável de obra</label>
                                <input aria-label="Nome do responsável" required={acessoExterno} maxLength={200} value={nomeResponsavel} onChange={e => setNomeResponsavel(e.target.value)}
                                    placeholder="Nome do responsável" style={{ marginBottom: 8 }} />
                                {!editandoFicha && (
                                    <>
                                        <div style={sigBoxStyle}>
                                            <SignatureCanvas ref={sigResponsavelRef} penColor="#1a202c"
                                                canvasProps={{ width: sigWidth, height: 130 }} />
                                        </div>
                                        <button type="button" className="btn-secondary" style={{ marginTop: 6, padding: '6px 12px', fontSize: 13 }}
                                            onClick={() => limparAssinatura(sigResponsavelRef)}><Icon name="clear" fallback="🧽" /> Limpar</button>
                                    </>
                                )}
                            </div>

                            {/* Pendências: avisar os intervenientes ao gravar, com o PDF em anexo */}
                            {!acessoExterno && ehPendencia && (
                                <div className="va-form-group" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700 }}>
                                        <input type="checkbox" checked={enviarEmail} onChange={e => setEnviarEmail(e.target.checked)}
                                            style={{ width: 20, height: 20 }} />
                                        ✉️ Enviar o relatório aos intervenientes
                                    </label>

                                    {enviarEmail && (
                                        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'normal',
                                                opacity: obraSel?.email_cliente ? 1 : 0.55 }}>
                                                <input type="checkbox" checked={enviarCliente && !!obraSel?.email_cliente}
                                                    disabled={!obraSel?.email_cliente}
                                                    onChange={e => setEnviarCliente(e.target.checked)} style={{ width: 18, height: 18 }} />
                                                <span>
                                                    <strong>Responsável do cliente</strong><br />
                                                    <small style={{ color: '#4a5568' }}>
                                                        {obraSel?.email_cliente
                                                            ? `${obraSel.nome_cliente ? obraSel.nome_cliente + ' · ' : ''}${obraSel.email_cliente}`
                                                            : 'sem email definido na pendência'}
                                                    </small>
                                                </span>
                                            </label>

                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'normal',
                                                opacity: obraSel?.email_fiscalizacao ? 1 : 0.55 }}>
                                                <input type="checkbox" checked={enviarFiscalizacao && !!obraSel?.email_fiscalizacao}
                                                    disabled={!obraSel?.email_fiscalizacao}
                                                    onChange={e => setEnviarFiscalizacao(e.target.checked)} style={{ width: 18, height: 18 }} />
                                                <span>
                                                    <strong>Fiscalização</strong><br />
                                                    <small style={{ color: '#4a5568' }}>
                                                        {obraSel?.email_fiscalizacao
                                                            ? `${obraSel.nome_fiscalizacao ? obraSel.nome_fiscalizacao + ' · ' : ''}${obraSel.email_fiscalizacao}`
                                                            : 'sem email definido na pendência'}
                                                    </small>
                                                </span>
                                            </label>

                                            <div>
                                                <small style={{ fontWeight: 600, color: '#4a5568' }}>Outros emails (pontuais)</small>
                                                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                                    <input type="email" value={emailExtraInput} placeholder="email@exemplo.pt"
                                                        onChange={e => setEmailExtraInput(e.target.value)}
                                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); juntarEmailExtra(); } }}
                                                        style={{ flex: 1 }} />
                                                    <button type="button" className="btn-secondary" onClick={juntarEmailExtra}
                                                        style={{ padding: '0 16px' }}><Icon name="plus" fallback="➕" /></button>
                                                </div>
                                                {emailsExtra.length > 0 && (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                                        {emailsExtra.map(em => (
                                                            <span key={em} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#edf2f7', border: '1px solid #cbd5e0', borderRadius: 14, padding: '4px 10px', fontSize: 13 }}>
                                                                {em}
                                                                <button type="button" onClick={() => setEmailsExtra(emailsExtra.filter(x => x !== em))}
                                                                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#e53e3e', fontWeight: 'bold', padding: 0 }}>×</button>
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <small style={{ color: '#718096' }}>
                                                Segue o relatório em PDF com fotos e assinaturas. Fica sempre cópia interna.
                                            </small>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="va-actions">
                                <button type="submit" className="va-submit-btn" disabled={isSubmitting || aPrepararAnexos}>
                                    <Icon name="save" fallback="" />{isSubmitting
                                        ? (editandoFicha ? 'A atualizar…' : 'A submeter…')
                                        : (editandoFicha ? 'Atualizar Ficha e Regenerar PDF' : 'Submeter Ficha e Gerar PDF')}
                                </button>
                            </div>

                            </>)}
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

const FichaManutencao = (props) => <MaquetesThemeProvider><OrcamentoFeedbackProvider><FichaManutencaoContent {...props} /></OrcamentoFeedbackProvider></MaquetesThemeProvider>;
export default FichaManutencao;
